'use client';
import type { Fabric, FabricGroup, MonogramColor, Option, OptionValue } from '@susu/custom-made/utils/interfaces';
import { onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { SyntheticEvent } from 'react';
import { memo, useRef } from 'react';
import styles from './card.module.scss';

export enum CardSize {
  Medium = 'medium',
  Small = 'small',
}

export enum CardOrientation {
  Default = 'default',
  Vertical = 'vertical',
}

export enum CardInfoIconType {
  Light = 'light',
  Dark = 'dark',
}

export type typeCard = Fabric | FabricGroup | Option | OptionValue | MonogramColor;

interface CardProps {
  card: typeCard;
  cardImages: string[];
  cardText: string;
  tabIndex?: number;
  cardSubText?: string;
  cardSize?: CardSize;
  cardOrientation?: CardOrientation;
  isParent?: boolean;
  isSelected?: boolean;
  hideMobileInfoText?: boolean;
  showInfoIcon?: boolean;
  showTooltip?: boolean;
  cardInfoIconType?: CardInfoIconType;
  cardItemAmount?: number;
  cardPrice?: string;
  isPresentationOnly?: boolean;
  showWarning?: boolean;
  cardFallbackImageUrl?: string;
  cardClick: (card: typeCard) => void;
  infoIconClick?: (card: typeCard) => void;
}

export const Card = memo(({
  card,
  cardImages,
  cardText,
  tabIndex,
  cardSubText,
  cardSize,
  cardOrientation,
  isParent,
  isSelected,
  hideMobileInfoText,
  showInfoIcon,
  showTooltip,
  cardInfoIconType,
  cardItemAmount,
  cardPrice,
  isPresentationOnly,
  showWarning,
  cardFallbackImageUrl,
  cardClick,
  infoIconClick,
}: CardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const infoClicked = (e: React.BaseSyntheticEvent) => {
    if (showTooltip) {
      //Show tooltip
      const elementRect = e.target.getBoundingClientRect();

      cardRef.current?.dispatchEvent(
        new CustomEvent('tooltipOpened', {
          detail: {
            title: cardText,
            description: cardSubText,
            topPosition: elementRect.top,
            leftPosition: elementRect.left,
            width: elementRect?.width,
          },
          bubbles: true,
        }),
      );
    } else if (infoIconClick) {
      infoIconClick(card);
    }
  };

  const onImageFailed = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    if (cardFallbackImageUrl && e.currentTarget.src !== cardFallbackImageUrl) {
      e.currentTarget.src = cardFallbackImageUrl;
    }
  };

  return (
    <div
      className={cn(
        styles.cardContainer,
        styles[cardSize ?? CardSize.Medium],
        styles[cardOrientation ?? CardOrientation.Default],
        {
          [styles.isSelected]: isSelected,
          [styles.isParent]: isParent,
          [styles.isPresentationOnly]: isPresentationOnly,
          [styles.isWarning]: showWarning,
        },
      )}
      ref={cardRef}
      data-test-id={card.id}
      onClick={() => cardClick(card)}
      onKeyDown={e => {
        onEnter(e, () => {
          cardClick(card);
        });
      }}
      tabIndex={tabIndex}
    >
      <div className={cn(styles.cardImageContainer, styles['images-' + cardImages.length])}>
        {cardImages.map((imagePath: string) => (
          <img
            key={imagePath}
            className={styles.cardImage}
            loading="lazy"
            src={imagePath}
            alt={imagePath}
            onError={onImageFailed}
          />
        ))}

        {cardPrice && (
          <div className={styles.priceContainer} data-test-id="card-price">
            {cardPrice}
          </div>
        )}
        {(showInfoIcon || showTooltip) && (
          <div
            onKeyDown={e => {
              onEnter(e, infoClicked);
            }}
            onClick={infoClicked}
            className={styles.infoSpaceContainer}
          >
            <div
              className={cn(styles.infoIconContainer, styles[cardInfoIconType ?? CardInfoIconType.Light], {
                [styles.toolTipIcon]: showTooltip,
              })}
              data-test-id="info-btn"
            >
              <i className="susu-icons" translate="no">
                info
              </i>
            </div>
          </div>
        )}
        {cardItemAmount && (
          <div className={styles.itemAmountContainer} data-test-id="item-amount">
            {cardItemAmount}
          </div>
        )}
      </div>
      <div
        className={cn(styles.cardDetails, {
          [styles.hideMobileInfoText]: hideMobileInfoText,
        })}
      >
        <div className={styles.cardText} data-test-id="card-text">
          {cardText}
        </div>
        {cardSubText && (
          <div className={styles.cardSubText} data-test-id="card-subtext">
            {cardSubText}
          </div>
        )}
      </div>
      {isParent && (
        <div className={styles.arrowIcon}>
          <i className="susu-icons" translate="no">
            arrow_right
          </i>
        </div>
      )}
    </div>
  );
});
